import { reportAccountDisplayName } from "../utils/localizedAccountDisplayName";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { apiGet } from "../api/client";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeftIcon, XMarkIcon } from "@heroicons/react/24/solid";
import { companyRoute } from "../utils/routeHelpers";
import { ymdTodayLocal } from "../utils/dateUtils";
import { formatNumber, formatMoney } from "../utils/formatNumber";
import { ReportDateRangeFilter } from "../components/ReportDateRangeFilter";
import { A, pillClass } from "../styles/appleClasses";

type AccountSum = { name: string; kind?: string; total: number; account_id?: number | null; account_type?: string | null; account_kind?: string | null; presentation_key?: string | null };
type Section = { accounts: AccountSum[]; total: number };

type IncomeStatement = {
  period: { start: string; end: string };
  income: Section;
  cogs: Section;
  gross_profit: number;
  operating_expense: Section;
  other_income: Section;
  other_expense: Section;
  operating_income: number;
  net_income: number;
};

function money(n: number | undefined | null) {
  return typeof n === "number" && !Number.isNaN(n)
    ? formatMoney(n)
    : "—";
}

export default function IncomeStatement() {
  const { t } = useTranslation();
  const [data, setData] = useState<IncomeStatement | null>(null);
  const [from, setFrom] = useState<string>("");
  const [to, setTo] = useState<string>(() => ymdTodayLocal());

  const [cfgLoading, setCfgLoading] = useState<boolean>(true);
  const [stmtLoading, setStmtLoading] = useState<boolean>(true);
  const [cfgEnd, setCfgEnd] = useState<string>(""); // ✅ guards “fetch with stale config”
  const [error, setError] = useState<string | null>(null);

  const loading = useMemo(
    () => cfgLoading || stmtLoading,
    [cfgLoading, stmtLoading],
  );

  const navigate = useNavigate();
  const { company_id = "" } = useParams<{ company_id: string }>();
  const todayStr = useMemo(() => ymdTodayLocal(), []);

// 1) load fiscal default start for the selected "to" date
useEffect(() => {
  let mounted = true;
  const toAtRequest = to; // ✅ capture the user's chosen end date for this request

  setCfgLoading(true);
  setError(null);
  setCfgEnd(""); // prevents statement fetch until config is ready for this request

  (async () => {
    try {
      const cfg = await apiGet<{
        default_start_for_end: string;
        end: string;
      }>(`/year-end/config?end=${toAtRequest}`);
      if (!mounted) return;

      setFrom(cfg.default_start_for_end);

      // ✅ critical: mark config ready for the *requested* end date,
      // not the backend-normalized quarter end
      setCfgEnd(toAtRequest);
    } catch {
      if (!mounted) return;

      // ✅ base fallback year on the requested end date (not "now")
      const d = new Date(`${toAtRequest}T00:00:00`);
      setFrom(`${d.getFullYear()}-01-01`);
      setCfgEnd(toAtRequest);
    } finally {
      if (!mounted) return;
      setCfgLoading(false);
    }
  })();

  return () => {
    mounted = false;
  };
}, [to]);

  // 2) fetch statement (only when config is ready for this `to`)
  useEffect(() => {
    if (!from || !to) return;
    if (cfgLoading) return;
    if (cfgEnd !== to) return; // ✅ critical: blocks “typing end date” thrash

    let mounted = true;
    setStmtLoading(true);
    setError(null);

    apiGet<IncomeStatement>(`/reports/income-statement?start=${from}&end=${to}`)
      .then((res) => {
        if (!mounted) return;
        setData(res);
      })
      .catch(() => {
        if (!mounted) return;
        setError("Failed to load income statement.");
        setData(null);
      })
      .finally(() => {
        if (!mounted) return;
        setStmtLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [from, to, cfgLoading, cfgEnd]);

  const iconBtn =
    `${A.iconBtn} inline-flex items-center justify-center ` +
    "hover:bg-gray-100 active:bg-gray-200 " +
    "focus:outline-none focus:ring-2 focus:ring-appleblueD";

  const TitleBar = () => (
    <div className="sticky top-0 z-30 bg-gray-50 w-full max-w-3xl flex items-center border-b border-gray-100 px-2 pb-2 mb-2">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className={iconBtn}
        aria-label={t("directory.common.back")}
      >
        <ArrowLeftIcon className="w-5 h-5 text-gray-700" />
      </button>

      <div className="flex-1 flex justify-center pointer-events-none relative">
        <h2 className={`${A.title} text-center`}>{t("incomeStatement.title")}</h2>
      </div>

      <button
        type="button"
        onClick={() => navigate(companyRoute(company_id, "/"))}
        className={iconBtn}
        aria-label={t("directory.common.close")}
      >
        <XMarkIcon className="w-5 h-5 text-gray-700" />
      </button>
    </div>
  );

  const Card = ({
    title,
    children,
    totalLabel,
    totalValue,
  }: {
    title: string;
    children: React.ReactNode;
    totalLabel?: string;
    totalValue?: number;
  }) => (
    <div className={`${A.card} p-5 md:p-6`}>
      <div className={`${A.title} mb-3`}>{title}</div>

      {children}

      {typeof totalValue === "number" && (
        <>
          <div className="border-t border-gray-200 my-3" />
          <div className="flex justify-between items-center text-[13px]">
            <span className="font-semibold text-gray-700">{totalLabel}</span>
            <span className={`font-semibold text-gray-700 ${A.num}`}>
              {money(totalValue)}
            </span>
          </div>
        </>
      )}
    </div>
  );

  const Row = ({
    label,
    value,
    strong = false,
  }: {
    label: string;
    value: number;
    strong?: boolean;
  }) => (
    <div className="flex justify-between items-baseline py-1 text-[13px]">
      <span
        className={strong ? "font-semibold text-gray-700" : "text-gray-700"}
      >
        {label}
      </span>
      <span
        className={[
          A.num,
          strong ? "font-semibold" : "font-medium",
          "text-gray-700",
        ].join(" ")}
      >
        {money(value)}
      </span>
    </div>
  );

  const SectionLabel = ({ children }: { children: React.ReactNode }) => (
    <div className={`${A.label} font-medium mb-1`}>{children}</div>
  );

  const accountLabel = (a: AccountSum) => reportAccountDisplayName({
    name: a.name, type: a.account_type, kind: a.account_kind,
    account_id: a.account_id, presentation_key: a.presentation_key,
  }, t, a.name);

  const renderAccounts = (section?: Section) => {
    const items = (section?.accounts || []).filter(
      (a) => Number(a.total || 0) !== 0,
    );
    if (items.length === 0)
      return <div className="text-[13px] text-gray-400">—</div>;

    return (
      <div className="space-y-1">
        {items.map((a, index) => (
          <div key={`${a.account_id ?? a.presentation_key ?? "legacy"}-${index}`} className="flex justify-between text-[13px] px-0.5">
            <span className="text-gray-700 truncate max-w-[68%]">{accountLabel(a)}</span>
            <span className={`font-medium text-gray-700 ${A.num}`}>
              {money(Number(a.total || 0))}
            </span>
          </div>
        ))}
      </div>
    );
  };

  const income = data?.income;
  const cogs = data?.cogs;
  const operating_expense = data?.operating_expense;
  const other_income = data?.other_income;
  const other_expense = data?.other_expense;

  const grossProfit = useMemo(() => {
    if (!data) return 0;
    return typeof data.gross_profit === "number"
      ? data.gross_profit
      : Number(income?.total || 0) - Number(cogs?.total || 0);
  }, [data, income?.total, cogs?.total]);

  const operatingIncome = useMemo(() => {
    if (!data) return 0;
    return typeof data.operating_income === "number"
      ? data.operating_income
      : grossProfit - Number(operating_expense?.total || 0);
  }, [data, grossProfit, operating_expense?.total]);

  const netIncome = useMemo(() => {
    if (!data) return 0;
    return typeof data.net_income === "number"
      ? data.net_income
      : operatingIncome +
          Number(other_income?.total || 0) -
          Number(other_expense?.total || 0);
  }, [data, operatingIncome, other_income?.total, other_expense?.total]);

  const otherRows: AccountSum[] = useMemo(() => {
    const oi = (other_income?.accounts || []).filter(
      (a) => Number(a.total || 0) !== 0,
    );
    const oe = (other_expense?.accounts || []).filter(
      (a) => Number(a.total || 0) !== 0,
    );
    return [
      ...oi.map((a) => ({ ...a, total: Number(a.total || 0) })),
      ...oe.map((a) => ({ ...a, total: -Number(a.total || 0) })),
    ].filter((r) => Number(r.total || 0) !== 0);
  }, [other_income?.accounts, other_expense?.accounts]);

  const otherNet = useMemo(() => {
    return Number(other_income?.total || 0) - Number(other_expense?.total || 0);
  }, [other_income?.total, other_expense?.total]);

  const showOtherSection = useMemo(() => {
    return (
      otherRows.length > 0 ||
      Number(other_income?.total || 0) !== 0 ||
      Number(other_expense?.total || 0) !== 0
    );
  }, [otherRows.length, other_income?.total, other_expense?.total]);

  const renderOtherRows = () => {
    if (otherRows.length === 0)
      return <div className="text-[13px] text-gray-400">—</div>;
    return (
      <div className="space-y-1">
        {otherRows.map((a, index) => (
          <div key={`${a.account_id ?? a.presentation_key ?? "legacy"}-${index}`} className="flex justify-between text-[13px] px-0.5">
            <span className="text-gray-700 truncate max-w-[68%]">{accountLabel(a)}</span>
            <span className={`font-medium text-gray-700 ${A.num}`}>
              {money(Number(a.total || 0))}
            </span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div
      className={`min-h-screen bg-gray-50 ${A.body} flex flex-col items-center pt-4`}
    >
      <TitleBar />

      {/* Date Range (always mounted, like CashAnalysis) */}
      <div className="w-full max-w-3xl px-2 mb-3">
        <ReportDateRangeFilter
          fromLabel={t("sales.shared.from")}
          toLabel={t("sales.shared.to")}
          openCalendarLabel={t("sales.shared.openCalendar")}
          companyId={company_id}
          startDate={from || ""}
          endDate={to}
          onChange={({ startDate, endDate }) => {
            setFrom(startDate);
            setTo(endDate);
          }}
        />
      </div>

      <div className="w-full max-w-3xl px-2 space-y-3 mb-10">
        {loading && data ? (
          <div className="flex justify-end">
            <div className={pillClass("neutral")}>{t("generalJournal.updating")}</div>
          </div>
        ) : null}

        {loading && !data ? (
          <div className={`${A.card} p-6 text-gray-400`}>{t("directory.common.loading")}</div>
        ) : error ? (
          <div className={`${A.card} p-6`}>
            <div className="text-[13px] font-medium text-red-600">{error === "Failed to load income statement." ? t("incomeStatement.errors.load") : error}</div>
          </div>
        ) : data ? (
          <>
            <Card
              title={t("incomeStatement.revenue")}
              totalLabel={t("incomeStatement.grossProfit")}
              totalValue={grossProfit}
            >
              <Row label={t("dashboard.metrics.sales")} value={Number(income?.total || 0)} />
              <Row
                label={t("incomeStatement.costOfGoodsSold", "Cost of Goods Sold")}
                value={Number(cogs?.total || 0)}
              />
              {cogs?.accounts.some(
                (account) => account.kind === "non_inventory_cogs" && Number(account.total) !== 0,
              ) && (
                <div data-testid="cogs-account-breakdown" className="mt-2 pl-3">
                  {renderAccounts(cogs)}
                </div>
              )}
            </Card>

            <Card
              title={t("incomeStatement.operatingExpenses")}
              totalLabel={t("incomeStatement.totalOperatingExpenses")}
              totalValue={Number(operating_expense?.total || 0)}
            >
              {renderAccounts(operating_expense)}
            </Card>

            <div className={`${A.card} p-5 md:p-6`}>
              <div className="flex justify-between items-center">
                <span className="font-semibold text-gray-700">
                  {t("incomeStatement.operatingIncome")}
                </span>
                <span
                  className={[
                    A.num,
                    "font-semibold",
                    operatingIncome >= 0 ? "text-gray-700" : "text-red-700",
                  ].join(" ")}
                >
                  {money(operatingIncome)}
                </span>
              </div>
            </div>

            {showOtherSection && (
              <Card
                title={t("incomeStatement.otherIncomeExpense")}
                totalLabel={t("incomeStatement.netOtherIncomeExpense")}
                totalValue={otherNet}
              >
                <SectionLabel>{t("incomeStatement.breakdown")}</SectionLabel>
                {renderOtherRows()}
              </Card>
            )}

            <div className={`${A.card} p-5 md:p-6`}>
              <div className="flex justify-between items-center">
                <span className="font-semibold text-gray-700">{t("incomeStatement.netIncome")}</span>
                <span
                  className={[
                    A.num,
                    "font-semibold",
                    netIncome >= 0 ? "text-appleblue" : "text-red-700",
                  ].join(" ")}
                >
                  {money(netIncome)}
                </span>
              </div>
            </div>
          </>
        ) : (
          <div className={`${A.card} p-6 text-gray-400`}>{t("balanceSheet.empty")}</div>
        )}
      </div>
    </div>
  );
}
